import * as moment from 'moment';

enum StoreName {
  Amazon = 'Amazon',
  Bigcommerce = 'Bigcommerce',
  ChannelAdvisor = 'ChannelAdvisor',
  CSV = 'CSV Import',
  eBay = 'eBay',
  ePages = 'ePages',
  Etsy = 'Etsy',
  Facebook = 'Facebook',
  Godaddy = 'GoDaddy',
  Magento = 'Magento',
  Magento_2 = 'Magento 2',
  Selz = 'Selz',
  Shopify = 'Shopify',
  Shippo = 'Shippo',
  Spreecommerce = 'Spreecommerce',
  Square = 'Square',
  Squarespace = 'Squarespace',
  Stripe = 'Stripe',
  Walmart = 'Walmart',
  Weebly = 'Weebly',
  Wix = 'Wix',
  WooCommerce = 'WooCommerce',
}

// https://shippo.atlassian.net/wiki/spaces/PD/pages/1246199929/Order+Splitting
export const StoresEligibleForSplit: Set<string> = new Set([
  StoreName.Shopify,
  StoreName.Amazon,
  StoreName.Bigcommerce,
  StoreName.Magento_2,
  StoreName.ChannelAdvisor,
  StoreName.Wix,
  StoreName.Walmart,
]);

enum StoreStatusName {
  Connected = 'Connected',
  Revoked = 'Revoked',
  Failed = 'Failed',
  InProgress = 'InProgress',
}

type StorePlatform = {
  name: StoreName;
};

type StoreStatus = {
  name: StoreStatusName;
  description: string;
};

type Store = {
  objectId: string;
  storePlatform: StorePlatform;
  status: StoreStatus;
  lastSyncTime: moment.Moment;
  objectCreated: moment.Moment;
  name: Maybe<string>;
  website: Maybe<string>;
  // Only used by some stores to go to the edit pages
  editObjectId?: string;
  isAutoFulfill: boolean;
  isFulfillEmailEnabled: boolean;
  isImportPosOrdersEnabled: boolean;
  expirationDate: Maybe<moment.Moment>;
  displayName: string;
};

type jsonStore = {
  object_id: string;
  store_platform: {
    name: string;
  };
  status: {
    name: string;
    description: string;
  };
  last_sync_time: string;
  object_created: string;
  name: Maybe<string>;
  website: Maybe<string>;
  is_auto_fulfill: Maybe<boolean>;
  is_fulfill_email_enabled: Maybe<boolean>;
  is_import_pos_orders_enabled: Maybe<boolean>;
  expiration_date: Maybe<string>;
  display_name?: string;
};

type jsonLegacyStore = {
  credentials_object_id: string;
  is_connected: boolean;
  shop_name: string;
};

function fromJSON(jsonStore: jsonStore): Store {
  // Some stores do not have status object
  // Even if they are connected, so defaulting to a connected status
  const status = jsonStore.status
    ? jsonStore.status
    : {
        name: StoreStatusName.Connected,
        description: 'Fallback to connected',
      };

  return {
    objectId: jsonStore.object_id,
    storePlatform: {
      name: StoreName[jsonStore.store_platform?.name?.replace(' ', '') as keyof typeof StoreName],
    },
    status: {
      name: StoreStatusName[status.name as keyof typeof StoreStatusName],
      description: status.description,
    },
    lastSyncTime: moment(jsonStore.last_sync_time),
    objectCreated: moment(jsonStore.object_created),
    name: jsonStore.name,
    website: jsonStore.website,
    isAutoFulfill: jsonStore.is_auto_fulfill || false,
    isFulfillEmailEnabled: jsonStore.is_fulfill_email_enabled || false,
    isImportPosOrdersEnabled: jsonStore.is_import_pos_orders_enabled || false,
    expirationDate: jsonStore.expiration_date ? moment(jsonStore.expiration_date) : null,
    displayName: jsonStore.display_name ?? '',
  };
}

// Parses from the /shops endpoint to a Store object only if the store is connected
// This is to support non-migrated stores
function fromJSONLegacyStore(jsonLegacyStore: jsonLegacyStore): Maybe<Store> {
  if (jsonLegacyStore.is_connected) {
    return {
      objectId: '',
      storePlatform: {
        name: Object.values(StoreName).find(
          (storeName) => storeName.toUpperCase() === jsonLegacyStore.shop_name.toUpperCase(),
        )!,
      },
      status: {
        name: StoreStatusName.Connected,
        description: '',
      },
      lastSyncTime: moment(),
      objectCreated: moment(),
      name: '',
      website: '',
      editObjectId: jsonLegacyStore.credentials_object_id,
      isAutoFulfill: false,
      isFulfillEmailEnabled: false,
      isImportPosOrdersEnabled: false,
      expirationDate: null,
      displayName: '',
    };
  }

  return null;
}

// this should be in constants/store.ts but is causing a circular dependency
const storesFilterValues = {
  [StoreName.Amazon]: 'Amazon',
  [StoreName.Bigcommerce]: 'Bigcommerce',
  [StoreName.ChannelAdvisor]: 'ChannelAdvisor',
  [StoreName.CSV]: 'CSV_Import',
  [StoreName.eBay]: 'eBay',
  [StoreName.ePages]: 'ePages',
  [StoreName.Etsy]: 'Etsy',
  [StoreName.Facebook]: 'Facebook',
  [StoreName.Godaddy]: 'Godaddy',
  [StoreName.Magento]: 'Magento',
  [StoreName.Magento_2]: 'Magento_2',
  [StoreName.Shopify]: 'Shopify',
  [StoreName.Shippo]: 'Shippo',
  [StoreName.Spreecommerce]: 'Spree',
  [StoreName.Square]: 'Square',
  [StoreName.Squarespace]: 'Squarespace',
  [StoreName.Stripe]: 'StripeRelay',
  [StoreName.Walmart]: 'Walmart',
  [StoreName.Weebly]: 'Weebly',
  [StoreName.Wix]: 'Wix',
  [StoreName.WooCommerce]: 'WooCommerce',
} as const;

const filterStoreNameValues = Object.keys(storesFilterValues).reduce<Record<string, string>>(
  (reversed, key) => {
    return {
      ...reversed,
      [storesFilterValues[key as keyof typeof storesFilterValues]]: key,
    };
  },
  {},
);

function getStoreFilterValue(storeName: StoreName): string {
  return storesFilterValues[storeName as keyof typeof storesFilterValues] as string;
}

function getStoreNameByFilter(filter: string): StoreName {
  return filterStoreNameValues[filter] as StoreName;
}

function isNewStore(store: Store): boolean {
  // When we connect a new store we set last sync time to a day in the past,
  // In general is a couple of weeks, but checking against 1 week should be enough.
  // Also checking that the store is a fresh store, to avoid matching with stores
  // That never synced
  return (
    store.lastSyncTime.isBefore(moment().subtract(1, 'week')) &&
    store.objectCreated.isAfter(moment().subtract(10, 'minutes'))
  );
}

function isSupportedStore(storeName: string): boolean {
  return Object.values(StoreName)
    .map((storeName) => storeName.toUpperCase())
    .includes(storeName.toUpperCase());
}

export default Store;

function isStoreLineItemEditable(shopApp: string): boolean {
  return [storesFilterValues[StoreName.CSV], storesFilterValues[StoreName.Shippo]].includes(
    shopApp as 'Shippo' | 'CSV_Import',
  );
}

export {
  storesFilterValues,
  filterStoreNameValues,
  StoreName,
  StoreStatusName,
  StorePlatform,
  StoreStatus,
  fromJSON,
  getStoreFilterValue,
  getStoreNameByFilter,
  isNewStore,
  isSupportedStore,
  isStoreLineItemEditable,
  fromJSONLegacyStore,
};
